import dayjs from 'dayjs';
import { CouncilRegion } from 'shared-components';
import {
	InsuranceLicenseData,
	NextBooking,
	PublicLiabilityInsuranceWithDocuments,
} from '../../Services/InsuranceLicenseService';
import {
	FoodLicenseFormErrors,
	FoodLicenseFormState,
	LiabilityFormErrors,
	LiabilityFormState,
} from './InsuranceLicenseTypes';

type BookingStatus = {
	label: 'expired' | 'not yet valid' | 'valid';
	className: 'error-tag' | 'warning-tag' | 'success-tag';
};

export const MAX_EVIDENCE_FILES = 5;
export const MAX_EVIDENCE_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export const formatUnixDate = (unixTime?: number | null) => {
	if (!unixTime) {
		return '';
	}
	return dayjs.unix(Number(unixTime)).format('D MMM YYYY');
};

export const toUnixDateString = (value: string) => {
	return String(dayjs(value).startOf('day').unix());
};

export const validateLiabilityForm = (form: LiabilityFormState): LiabilityFormErrors => {
	const errors: LiabilityFormErrors = {};
	if (!form.insuranceProvider.trim()) {
		errors.insuranceProvider = 'Please enter the name of your insurance provider';
	}
	if (!form.policyNumber.trim()) {
		errors.policyNumber = 'Please enter your policy number';
	}
	if (!form.validFrom) {
		errors.validFrom = 'Please enter your insurance valid from date';
	}
	if (!form.expiryDate) {
		errors.expiryDate = 'Please enter your insurance expiry date';
	}
	if (form.validFrom && form.expiryDate && dayjs(form.expiryDate).isBefore(dayjs(form.validFrom), 'day')) {
		errors.expiryDate = 'Your insurance expiry date must be after your insurance valid from date';
	}
	return errors;
};

export const validateFoodLicenseForm = (form: FoodLicenseFormState): FoodLicenseFormErrors => {
	const errors: FoodLicenseFormErrors = {};
	if (form.councilRegionId === '0') {
		errors.councilRegionId = 'Please select a council region';
	}
	if (!form.licenseNumber.trim()) {
		errors.licenseNumber = 'Please enter your license number';
	}
	if (!form.expiryDate) {
		errors.expiryDate = 'Please enter your license expiry date';
	}
	return errors;
};

export const listFormErrors = (errors: Record<string, string | undefined>) =>
	Object.values(errors).filter((error): error is string => Boolean(error));

export const formatFileSize = (bytes: number) => {
	if (bytes <= 0) {
		return '0 B';
	}
	const kb = 1024;
	const mb = kb * 1024;
	if (bytes >= mb) {
		return `${(bytes / mb).toFixed(1)} MB`;
	}
	return `${Math.ceil(bytes / kb)} KB`;
};

const fallbackImageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff', 'heic', 'heif', 'avif'];

const isSupportedEvidenceFile = (file: File) => {
	const mime = file.type.toLowerCase();
	if (mime.startsWith('image/')) {
		return true;
	}
	if (mime === 'application/pdf') {
		return true;
	}
	if (!mime) {
		const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
		if (extension === 'pdf') {
			return true;
		}
		return fallbackImageExtensions.includes(extension);
	}
	return false;
};

export const validateEvidenceFile = (file: File) => {
	if (file.size > MAX_EVIDENCE_FILE_SIZE_BYTES) {
		return `${file.name} exceeds the 10MB file size limit`;
	}
	if (!isSupportedEvidenceFile(file)) {
		return `${file.name} is not supported. Only image files and PDFs are allowed`;
	}
	return undefined;
};

export const validateEvidenceFiles = (files: File[], requireAtLeastOne = true) => {
	if (requireAtLeastOne && files.length === 0) {
		return 'Please upload at least one image or PDF file';
	}
	if (files.length > MAX_EVIDENCE_FILES) {
		return `You can upload up to ${MAX_EVIDENCE_FILES} files`;
	}
	for (const file of files) {
		const fileError = validateEvidenceFile(file);
		if (fileError) {
			return fileError;
		}
	}
	return undefined;
};

export const determineInsuranceValidity = (date: number, policies: PublicLiabilityInsuranceWithDocuments[]) => {
	if (policies.length === 0) {
		return false;
	}

	let valid = 0;
	let expired = 0;
	let notYetValid = 0;
	policies.forEach((policy) => {
		const expiry = Number(policy.PublicLiabilityInsuranceExpiry ?? 0);
		const validFrom = Number(policy.PublicLiabilityInsuranceValidFrom ?? 0);
		if (expiry < date) {
			expired += 1;
			return;
		}
		if (validFrom > date) {
			notYetValid += 1;
			return;
		}
		valid += 1;
	});

	if (valid > 0) {
		return true;
	}
	if (expired > 0 || notYetValid > 0) {
		return false;
	}
	return true;
};

export const getRequiredLicenseRegions = (data: InsuranceLicenseData) => {
	const nowUnix = dayjs().unix();
	let requiredRegions = data.regionsRequired
		.map((required) => data.councilRegions.find((region) => region.CouncilRegionId === required.CouncilRegionId))
		.filter((region): region is CouncilRegion => Boolean(region));

	data.foodLicenses.forEach((license) => {
		const expiry = Number(license.FoodLicenseExpiry ?? 0);
		if (expiry <= nowUnix) {
			return;
		}
		if (Number(license.FoodLicenseStatewide) === 1) {
			requiredRegions = [];
			return;
		}
		requiredRegions = requiredRegions.filter(
			(region) => region.CouncilRegionId !== Number(license.FoodLicenseCouncilRegionId ?? 0),
		);
	});

	return requiredRegions;
};

export const formatRegionList = (regions: CouncilRegion[]) => {
	const names = regions.map((region) => region.CouncilRegionName ?? 'Unknown');
	if (names.length === 0) {
		return '';
	}
	if (names.length === 1) {
		return `${names[0]} region`;
	}
	if (names.length === 2) {
		return `${names[0]} and ${names[1]} regions`;
	}
	return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]} regions`;
};

export const bookingHeader = (booking?: NextBooking) => {
	if (!booking) {
		return '';
	}
	return `Booking (${formatUnixDate(booking.MarketDayDate)})`;
};

export const bookingStatus = (
	policy: PublicLiabilityInsuranceWithDocuments,
	booking?: NextBooking,
): BookingStatus | null => {
	if (!booking) {
		return null;
	}
	const expiry = Number(policy.PublicLiabilityInsuranceExpiry ?? 0);
	const validFrom = Number(policy.PublicLiabilityInsuranceValidFrom ?? 0);
	const bookingDate = Number(booking.MarketDayDate ?? 0);

	if (expiry < bookingDate) {
		return { label: 'expired', className: 'error-tag' };
	}
	if (validFrom > bookingDate) {
		return { label: 'not yet valid', className: 'warning-tag' };
	}
	return { label: 'valid', className: 'success-tag' };
};
